/**
 * LockQueueScreen (/lock-queue/*) — the capital-markets rate-lock queue.
 *
 * CAPITAL MARKETS ONLY. `GET /pricing/all_locks` 403s for everyone else; this is
 * the one surface where the trade counterparty is shown and where locks are
 * reviewed / confirmed (the loan drilldown never shows counterparty — see
 * rate-locks.md + correspondent-admin-and-locks.md §4).
 *
 * Two layouts off one fetch of every lock doc (filtered/sorted client-side):
 *   - `/lock-queue`        → full-width queue table (AG Grid).
 *   - `/lock-queue/:id/*`  → master-detail: the filtered lock list as a left
 *     rail + the full Loan Drilldown embedded on the right (a row click selects
 *     a loan without leaving the queue). The drilldown derives its base path
 *     from the URL, so its tabs stay under `/lock-queue/:id`.
 *
 * Lanes are the `queueState` workflow states (pending_confirmation → active →
 * needs_pricing_review → closed). Visual language matches the Support board
 * (shared design tokens in screens/SupportScreen/components/supportUi.jsx).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Route, Routes, useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { AgGridReact } from "ag-grid-react";
import { ModuleRegistry } from "ag-grid-community";
import { MasterDetailModule, SetFilterModule } from "ag-grid-enterprise";

import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Container from "@mui/material/Container";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Tooltip from "@mui/material/Tooltip";
import ArrowBackRounded from "@mui/icons-material/ArrowBackRounded";
import CodeRounded from "@mui/icons-material/CodeRounded";
import EditOutlined from "@mui/icons-material/EditOutlined";
import FileDownloadOutlined from "@mui/icons-material/FileDownloadOutlined";
import InsertDriveFileOutlined from "@mui/icons-material/InsertDriveFileOutlined";
import LockOutlined from "@mui/icons-material/LockOutlined";
import OpenInNewRounded from "@mui/icons-material/OpenInNewRounded";
import RefreshRounded from "@mui/icons-material/RefreshRounded";
import ScheduleRounded from "@mui/icons-material/ScheduleRounded";
import WarningAmberRounded from "@mui/icons-material/WarningAmberRounded";

import AgGridTableWrapper from "../../components/agGrid/AgGridTableWrapper";
import {
  INACTIVE_TOOLTIP,
  PillGroup,
  QueueScopeRow,
  QueueStatusBar,
  ToolbarSwitch,
  dateOnlyComparator,
  fetchLoanIndex,
  loanStageColumnDefs,
  readHideInactive,
  withLoanStage,
  writeHideInactive,
} from "../../components/queueToolbar/queueToolbar";
import {
  losLocksApi,
  lockChannelOf,
  LOCK_QUEUE_STATES,
} from "../../api/losLocksApi";
import { usePermissionsHook } from "../../hooks/usePermissionsHook";
import { useEnsureUsers } from "../../hooks/useEnsureReferenceData";
import { useDocumentTitle } from "../../hooks/useDocumentTitle";
import { useLoanFilesHook } from "../../hooks/useLoanFilesHook";
import { formatDate } from "../../_src_shared/utils/functions/formatDate";
import { formatDateTime } from "../../_src_shared/utils/functions/formatDateTime";
import formatDollar from "../../_src_shared/utils/functions/formatDollar";
import { formatPercent } from "../../utils/numbers/formatPercent";
import {
  getLockProduct,
  isLockCurrentlyExpired,
} from "../LoanDrilldownScreen/tabs/LoanDrilldownLoanLockTab/loanLockUtils";
import LoanDrilldownScreen from "../LoanDrilldownScreen/LoanDrilldownScreen";
import LoanLockFormV2 from "../LoanDrilldownScreen/tabs/LoanDrilldownLoanLockTab/LoanLockFormV2";
import PostCloseQueueView, { ModeTabs } from "./PostCloseQueueView";
import { T } from "../SupportScreen/components/supportUi";
import SearchInput from "../../components/inputs/SearchInput";
import JsonPreview from "../../components/common/JsonPreview";
import {
  FlagOutlined,
  KeyboardArrowRightOutlined,
} from "@mui/icons-material";

// Master/detail (expandable rows) is an enterprise feature — register it here;
// AgGridTableWrapper only registers the community bundle.
ModuleRegistry.registerModules([MasterDetailModule, SetFilterModule]);

const ALL = "__all__";

// ── queue lanes (queueState) ─────────────────────────────────────────────────
// Order is intentional: Pending → Needs review → Active → Closed → All (the
// default-lane heuristic in LockQueueScreen leans on Pending/Needs-review first).
const LANES = [
  { key: LOCK_QUEUE_STATES.PENDING_CONFIRMATION, label: "Pending" },
  { key: LOCK_QUEUE_STATES.NEEDS_PRICING_REVIEW, label: "Needs review" },
  { key: LOCK_QUEUE_STATES.ACTIVE, label: "Active" },
  { key: LOCK_QUEUE_STATES.CLOSED, label: "Closed" },
  { key: ALL, label: "All" },
];

const LANE_META = {
  [LOCK_QUEUE_STATES.PENDING_CONFIRMATION]: {
    label: "Pending confirmation",
    bg: "#fef3e2",
    fg: T.amber,
  },
  [LOCK_QUEUE_STATES.ACTIVE]: { label: "Active", bg: "#effaf2", fg: T.green },
  [LOCK_QUEUE_STATES.NEEDS_PRICING_REVIEW]: {
    label: "Needs review",
    bg: "#fdecdf",
    fg: T.orange,
  },
  [LOCK_QUEUE_STATES.CLOSED]: { label: "Closed", bg: "#f1f4f6", fg: T.slate },
};

// Bottom-row lane pills (same order as LANES), each with its lane color dot.
const LANE_PILLS = LANES.map((l) => ({
  ...l,
  dot: l.key === ALL ? null : LANE_META[l.key]?.fg,
}));

const HIDE_INACTIVE_KEY = "lockQueue.hideInactiveLoans";

// lock_status workflow chip colors.
const STATUS_META = {
  Requested: { bg: "#fef3e2", fg: T.amber, dot: T.amber },
  Confirmed: { bg: "#effaf2", fg: T.green, dot: T.green },
  Repriced: { bg: "#eff4ff", fg: T.blue, dot: T.blue },
  Cancelled: { bg: "#f1f4f6", fg: T.slate, dot: T.slate },
  Expired: { bg: "#fdecec", fg: T.red, dot: T.red },
  Superseded: { bg: "#f1f4f6", fg: T.slate, dot: T.slate },
};

// Lock type (origination channel). A legacy lock carries only is_correspondent_lock,
// so `lockChannelOf` derives the label — a legacy DIRECT lock reads "Broker" until
// the desk sets it on the edit form (rate-locks.md §"Lock channel").
const LOCK_CHANNEL_META = {
  Direct: { bg: "#f1f4f6", fg: T.slate },
  Broker: { bg: "#eff4ff", fg: T.blue },
  Correspondent: { bg: T.teal50, fg: T.teal600 },
};

const PRODUCT_META = {
  dscr: { label: "DSCR", bg: "#eef2ff", fg: "#4f46e5" },
  rtl: { label: "RTL", bg: T.teal50, fg: T.teal600 },
};

// ── master-detail (split view) design tokens — from the approved mockup. The
// `/lock-queue` grid above keeps the Support-board palette (T); the split view's
// columns 1 + 2 use this reference palette. ───────────────────────────────────
const C = {
  bg: "#f4f6f8",
  surface: "#ffffff",
  surface2: "#f7f9fa",
  text: "#1f2430",
  text2: "#5d6675",
  text3: "#9aa3b2",
  border: "#e6e9ee",
  border2: "#d4d9e1",
  teal: "#1798a8",
  tealD: "#0f6e7a",
  navy: "#1f2a3c",
  navyHover: "#2a384e",
  successT: "#0f6e56",
  successBg: "#e1f5ee",
  warnT: "#854f0b",
  warnBg: "#faeeda",
  dangerT: "#a32d2d",
  dangerBg: "#fceceb",
  dangerBorder: "#f3c9c9",
  infoT: "#185fa5",
  radius: "10px",
  radiusSm: "7px",
};
const MONO = "ui-monospace,SFMono-Regular,Menlo,Consolas,monospace";

// lock_status → list dot color + detail status-pill colors (split view).
const SPLIT_STATUS = {
  Confirmed: { dot: "#3b6d11", fg: C.successT, bg: C.successBg },
  Repriced: { dot: "#3b6d11", fg: C.successT, bg: C.successBg },
  Requested: { dot: "#ba7517", fg: C.warnT, bg: C.warnBg },
  Cancelled: { dot: C.text3, fg: C.text2, bg: C.surface2 },
  Superseded: { dot: C.text3, fg: C.text2, bg: C.surface2 },
  Expired: { dot: C.dangerT, fg: C.dangerT, bg: C.dangerBg },
};

// ── lock-doc helpers (raw snake_case rows from /pricing/all_locks) ────────────
const oid = (v) => (v && typeof v === "object" && v.$oid ? v.$oid : v);

const productOf = (lock) => {
  if (lock?.product) return String(lock.product).toLowerCase();
  const fromType = getLockProduct(lock?.loan_terms_at_lock?.loanProductType);
  if (fromType) return fromType;
  // Fall back to the field shape: RTL carries a coupon, DSCR a bond price.
  if (lock?.coupon_rate != null) return "rtl";
  if (lock?.rate_at_lock != null || lock?.base_price_for_rate != null)
    return "dscr";
  return null;
};

const personName = (obj) => obj?.fullName || obj?.emailAddress || null;

const loanAmountOf = (lock) => {
  const t = lock?.loan_terms_at_lock || {};
  return t.totalLoanAmount ?? t.baseLoanAmount ?? null;
};

const lockKey = (l) =>
  oid(l._id) || `${oid(l.dealId)}-${l.lock_status}-${l.loanNumber}`;

const MS_DAY = 86400000;
// { state: "expired" | "soon" | "ok" | "none", days, label }
const expirationInfo = (lock) => {
  const raw = lock?.lock_expiration_date;
  if (!raw) return { state: "none", label: "—" };
  const exp = new Date(raw);
  if (isNaN(exp.getTime())) return { state: "none", label: "—" };
  if (lock.lock_status === "Expired" || isLockCurrentlyExpired(lock))
    return { state: "expired", label: "Expired" };
  const days = Math.ceil((exp.getTime() - Date.now()) / MS_DAY);
  if (days <= 7)
    return { state: "soon", days, label: `${days}d left`, date: exp };
  return { state: "ok", days, label: formatDate(raw), date: exp };
};

const matchesSearch = (l, q) => {
  if (!q) return true;
  const hay = `${l.loanNumber || ""} ${oid(l.dealId) || ""} ${
    l.lock_counterparty || ""
  } ${personName(l.lock_request_user) || ""} ${l.assignedTo || ""}`.toLowerCase();
  return hay.includes(q);
};

// "Jun 5" — short month + day, no year (lock timeline rows).
const shortDate = (v) => {
  if (!v) return null;
  return formatDate(v, null, "MMM d");
};

// "Jul 5, 2026" — short month + day + year (expiration). Date-only field.
const medDate = (v) => {
  if (!v) return null;
  return formatDate(v, null, "MMM d, yyyy");
};

// Same display as medDate, but for UTC *timestamps* (createDateTime/
// updateDateTime): convert UTC → Eastern so the calendar day is correct rather
// than slicing the raw UTC date-part (which is off-by-one for early-UTC times).
const medDateTime = (v) => {
  if (!v) return null;
  return formatDateTime(v, "MMM d, yyyy") || null;
};

const fmtMB = (bytes) => {
  if (bytes == null) return "";
  const mb = bytes / 1048576;
  if (mb >= 1) return `${mb.toFixed(1)} MB`;
  return `${Math.max(1, Math.round(bytes / 1024))} KB`;
};

// loanName "EV29217 - 1951-1955 Hornblend St… - DSCR (Pre)" → { title, address }.
const parseLoanName = (loanName, loanNumber) => {
  const parts = String(loanName || "")
    .split(" - ")
    .map((s) => s.trim())
    .filter(Boolean);
  const title = parts[0] || (loanNumber != null ? `EV${loanNumber}` : "—");
  const address = parts.length >= 2 ? parts[1] : "";
  return { title, address };
};

// Person name resolved from the lock's approval objects (fallback to SF initials).
const lockedByOf = (lock) =>
  personName(lock?.lock_confirm_user) ||
  personName(lock?.lock_request_user) ||
  lock?.salesforce_locked_by ||
  null;

// Expiry urgency for the split view: { cc: "red"|"amber"|"ok"|"none", fg, bg }.
const splitUrgency = (exp) => {
  if (exp.state === "expired")
    return { cc: "red", fg: C.dangerT, bg: C.dangerBg };
  if (exp.state === "none") return { cc: "none", fg: C.text3, bg: C.surface2 };
  if ((exp.days ?? 99) <= 3)
    return { cc: "red", fg: C.dangerT, bg: C.dangerBg };
  if ((exp.days ?? 99) <= 7) return { cc: "amber", fg: C.warnT, bg: C.warnBg };
  return { cc: "ok", fg: C.successT, bg: C.successBg };
};

// snake/lower enum → "Title Case" (e.g. "salesforce_rate_lock" → "Salesforce
// Rate Lock", "active" → "Active").
const prettyEnum = (s) =>
  s
    ? String(s)
        .replace(/_/g, " ")
        .replace(/\b\w/g, (c) => c.toUpperCase())
    : null;

const yesNo = (v) => (v == null ? null : v ? "Yes" : "No");

const excListOf = (lock) =>
  Array.isArray(lock?.exceptions_at_lock) ? lock.exceptions_at_lock : [];
const actionNeededOf = (lock) =>
  lock?.actionNeeded?.reason ? lock.actionNeeded : null;

// ── exceptions / action-needed display (shared: table, detail pane, panel) ────
// Palette-neutral colors so these read correctly under both the queue (T) and
// split-view (C) surfaces.
const EXC = {
  border: "#e6e9ee",
  hardBg: "#fceceb",
  hardFg: "#a32d2d",
  softBg: "#faeeda",
  softFg: "#854f0b",
  chipBg: "#f1f4f6",
  chipFg: "#5d6675",
  ink: "#1f2430",
  muted: "#5d6675",
  faint: "#9aa3b2",
};

// actionNeeded ("Returned for action") — the single shared treatment used by the
// Loan Lock tab, the lock form, and this queue (table chip, master detail, split
// pane). Red, Flag icon, "Returned for action" + optional "Exception required".
const ACTION_NEEDED = {
  bg: "#FEF2F2",
  border: "#FECACA",
  fg: "#DC2626",
  ink: "#7F1D1D",
  title: "Returned for action",
};

const MiniChip = ({ bg, fg, children }) => (
  <Box
    component="span"
    sx={{
      display: "inline-block",
      fontSize: 10.5,
      fontWeight: 700,
      lineHeight: 1.5,
      color: fg,
      background: bg,
      borderRadius: "5px",
      px: "6px",
      py: "1px",
      whiteSpace: "nowrap",
    }}
  >
    {children}
  </Box>
);

// Action-needed banner — the desk returned the lock with a reason (and maybe a
// required exception). Shown on the detail pane + expanded panel.
const ActionNeededBanner = ({ action }) => {
  if (!action) return null;
  const flaggedMeta = [
    action.flaggedBy ? `Flagged by ${action.flaggedBy}` : null,
    action.flaggedDateTime ? formatDate(action.flaggedDateTime) : null,
  ]
    .filter(Boolean)
    .join(" · ");
  return (
    <Box
      sx={{
        background: ACTION_NEEDED.bg,
        border: `1px solid ${ACTION_NEEDED.border}`,
        borderRadius: "8px",
        p: "10px 12px",
        minWidth: 0,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: "8px", mb: "3px", flexWrap: "wrap" }}>
        <FlagOutlined sx={{ fontSize: 15, color: ACTION_NEEDED.fg, flexShrink: 0 }} />
        <Box sx={{ fontSize: 12.5, fontWeight: 700, color: ACTION_NEEDED.fg }}>
          {ACTION_NEEDED.title}
        </Box>
        {action.requiresException && (
          <Box
            component="span"
            sx={{
              fontSize: 10.5,
              fontWeight: 700,
              color: ACTION_NEEDED.fg,
              background: "#fff",
              border: `1px solid ${ACTION_NEEDED.border}`,
              borderRadius: "999px",
              px: "7px",
              py: "1px",
            }}
          >
            Exception required
          </Box>
        )}
      </Box>
      <Box
        sx={{
          fontSize: 12,
          color: ACTION_NEEDED.ink,
          lineHeight: 1.5,
          overflowWrap: "anywhere",
        }}
      >
        {action.reason}
      </Box>
      {flaggedMeta && (
        <Box sx={{ fontSize: 11, color: ACTION_NEEDED.ink, opacity: 0.85, mt: "4px" }}>
          {flaggedMeta}
        </Box>
      )}
    </Box>
  );
};

const ExceptionCard = ({ e }) => {
  const hard = !!e?.isHardException;
  return (
    <Box
      sx={{
        border: `1px solid ${EXC.border}`,
        borderRadius: "8px",
        p: "10px 12px",
        background: "#fcfdfe",
        minWidth: 0,
        width: "100%",
        boxSizing: "border-box",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "7px",
          flexWrap: "wrap",
          mb: "5px",
        }}
      >
        <Box
          sx={{
            fontSize: 12,
            fontWeight: 700,
            color: EXC.ink,
            overflowWrap: "anywhere",
          }}
        >
          {e?.exceptionCategory || "Exception"}
        </Box>
        <MiniChip
          bg={hard ? EXC.hardBg : EXC.softBg}
          fg={hard ? EXC.hardFg : EXC.softFg}
        >
          {hard ? "Hard" : "Soft"}
        </MiniChip>
        {e?.exceptionStatus && (
          <MiniChip bg={EXC.chipBg} fg={EXC.chipFg}>
            {e.exceptionStatus}
          </MiniChip>
        )}
      </Box>
      {e?.exceptionDescription && (
        <Box
          sx={{
            fontSize: 11.5,
            color: EXC.muted,
            lineHeight: 1.45,
            overflowWrap: "anywhere",
          }}
        >
          {e.exceptionDescription}
        </Box>
      )}
      {e?.riskBucket && (
        <Box sx={{ fontSize: 11, color: EXC.faint, mt: "4px" }}>
          Risk bucket: {e.riskBucket}
        </Box>
      )}
    </Box>
  );
};

const ExceptionsAtLockList = ({ exceptions }) =>
  exceptions.length ? (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
      {exceptions.map((e, i) => (
        <ExceptionCard key={oid(e?._id) || i} e={e} />
      ))}
    </Box>
  ) : null;

// ── chips ────────────────────────────────────────────────────────────────────
// One small rounded-rect tag for product / status / lane, so they all read as
// consistent compact chips (not stretched pills).
const Tag = ({ bg, fg, children }) => (
  <Box
    component="span"
    sx={{
      display: "inline-block",
      fontSize: "11.5px",
      fontWeight: 700,
      letterSpacing: ".02em",
      lineHeight: 1.5,
      color: fg,
      background: bg,
      borderRadius: "6px",
      px: "8px",
      py: "3px",
      whiteSpace: "nowrap",
    }}
  >
    {children}
  </Box>
);

const Dash = () => (
  <Box component="span" sx={{ color: T.ink300 }}>
    —
  </Box>
);

const StatusChip = ({ status }) => {
  const m = STATUS_META[status];
  if (!m) return <Dash />;
  return (
    <Tag bg={m.bg} fg={m.fg}>
      {status}
    </Tag>
  );
};

const LaneChip = ({ state }) => {
  const m = LANE_META[state];
  if (!m) return <Dash />;
  return (
    <Tag bg={m.bg} fg={m.fg}>
      {m.label}
    </Tag>
  );
};

const LockChannelTag = ({ channel }) => {
  const m = LOCK_CHANNEL_META[channel];
  if (!m) return <Dash />;
  return (
    <Tag bg={m.bg} fg={m.fg}>
      {channel}
    </Tag>
  );
};

const ProductTag = ({ product }) => {
  const m = PRODUCT_META[product];
  if (!m) return <Dash />;
  return (
    <Tag bg={m.bg} fg={m.fg}>
      {m.label}
    </Tag>
  );
};

// Expiry label, color-coded by urgency. `size` overrides the font size.
const ExpiresLabel = ({ lock, size }) => {
  const e = expirationInfo(lock);
  if (e.state === "expired")
    return (
      <Box
        component="span"
        sx={{ color: T.red, fontWeight: 600, fontSize: size || 13 }}
      >
        Expired
      </Box>
    );
  if (e.state === "soon")
    return (
      <Box
        component="span"
        sx={{ color: T.amber, fontWeight: 600, fontSize: size || 13 }}
      >
        {e.label}
      </Box>
    );
  return (
    <Box component="span" sx={{ color: T.ink500, fontSize: size || 12.5 }}>
      {e.label}
    </Box>
  );
};

const StatCard = ({ n, label, color }) => (
  <Box
    sx={{
      background: T.card,
      border: `1px solid ${T.line}`,
      borderRadius: T.radius,
      boxShadow: T.shadow,
      p: "16px 18px",
      display: "flex",
      alignItems: "center",
      gap: "14px",
    }}
  >
    <Box
      sx={{
        width: 4,
        alignSelf: "stretch",
        borderRadius: "4px",
        background: color,
      }}
    />
    <Box>
      <Box
        sx={{
          fontSize: 24,
          fontWeight: 800,
          letterSpacing: "-.02em",
          color: T.ink900,
        }}
      >
        {n}
      </Box>
      <Box sx={{ fontSize: 12, color: T.ink500, fontWeight: 500, mt: "1px" }}>
        {label}
      </Box>
    </Box>
  </Box>
);

const RefreshButton = ({ onClick, loading }) => (
  <Box
    component="button"
    type="button"
    onClick={onClick}
    disabled={loading}
    sx={{
      display: "inline-flex",
      alignItems: "center",
      gap: "8px",
      font: "inherit",
      fontSize: 13.5,
      fontWeight: 600,
      color: T.ink700,
      background: "#fff",
      border: `1px solid ${T.line}`,
      borderRadius: "8px",
      boxShadow: T.shadow,
      px: "14px",
      py: "9px",
      cursor: loading ? "default" : "pointer",
      opacity: loading ? 0.6 : 1,
      "&:hover": { borderColor: T.ink300 },
    }}
  >
    <RefreshRounded sx={{ fontSize: 18 }} />
    Refresh
  </Box>
);

const EmptyLocks = ({ hasAny }) => (
  <Box
    sx={{
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      gap: 1,
      py: 8,
      px: 4,
      textAlign: "center",
    }}
  >
    <LockOutlined sx={{ fontSize: 34, color: T.ink300 }} />
    <Box sx={{ fontSize: 15, fontWeight: 700, color: T.ink900 }}>
      {hasAny ? "No locks match this view" : "No locks yet"}
    </Box>
    <Box sx={{ fontSize: 13, color: T.ink500, maxWidth: 380 }}>
      {hasAny
        ? "Try a different lane or clear the search."
        : "Locks requested from a loan will appear here for review and confirmation."}
    </Box>
  </Box>
);

// ── expandable-row detail (master/detail) ────────────────────────────────────
const detailBtnSx = {
  display: "inline-flex",
  alignItems: "center",
  gap: "6px",
  font: "inherit",
  fontSize: 12.5,
  fontWeight: 600,
  borderRadius: "8px",
  px: "12px",
  py: "7px",
  cursor: "pointer",
  whiteSpace: "nowrap",
};

// "View more details" — navigates to the same master-detail the row used to open.
// Solid/filled so it reads as the primary action of the expanded row.
const ViewDetailsBtn = ({ onClick }) => (
  <Box
    component="button"
    type="button"
    onClick={onClick}
    sx={{
      ...detailBtnSx,
      fontSize: 13.5,
      px: "16px",
      py: "9px",
      color: "#fff",
      background: T.teal600,
      border: `1px solid ${T.teal600}`,
      boxShadow: T.shadow,
      "&:hover": { filter: "brightness(0.95)" },
    }}
  >
    View More Details
    <KeyboardArrowRightOutlined sx={{ fontSize: 16 }} />
  </Box>
);

// Opens the loan drilldown in a new browser tab (separate from the in-app split
// view that "View more details" navigates to).
const OpenLoanNewTabBtn = ({ dealId }) => (
  <Box
    component="a"
    href={`/loan/${dealId}`}
    target="_blank"
    rel="noreferrer"
    sx={{
      ...detailBtnSx,
      fontSize: 13.5,
      px: "16px",
      py: "9px",
      color: T.ink700,
      background: "#fff",
      border: `1px solid ${T.line}`,
      textDecoration: "none",
      "&:hover": { background: T.bg },
    }}
  >
    Open Loan In New Tab
    <OpenInNewRounded sx={{ fontSize: 16 }} />
  </Box>
);

// AG Grid detail cell renderer — one expanded panel per row. The form itself is
// LoanLockFormV2, the exact same component the drilldown Loan Lock sidebar
// renders, so the questions here always match the sidebar (single source of
// truth). We feed it a synthetic `loanDrilldown` built from the lock doc.
const LockDetailRenderer = ({
  data: lock,
  onViewDetails,
  onCollapse,
  onReload,
  maxWidth,
}) => {
  const dealId = oid(lock?.dealId);
  const rowId = lockKey(lock);
  const action = actionNeededOf(lock);
  const exceptions = excListOf(lock);

  // Minimal drilldown shape LoanLockFormV2 reads (id, product, lock, docs).
  const syntheticDrilldown = useMemo(
    () => ({
      _id: dealId,
      loanName: lock?.loanName,
      loanProductType: lock?.loan_terms_at_lock?.loanProductType,
      loanLock: lock,
      loanDocuments: [],
    }),
    [dealId, lock],
  );

  const handleDone = useCallback(() => {
    onCollapse?.(rowId);
    onReload?.();
  }, [onCollapse, onReload, rowId]);

  const showExceptions = exceptions.length > 0;

  // Exceptions-at-lock card (left column, under the notes). Fills its column width; the inner
  // cards wrap because the column track is width-constrained (not content-sized).
  const exceptionsCard = showExceptions ? (
    <Box
      sx={{
        background: "#fff",
        border: `1px solid ${T.line}`,
        borderRadius: T.radius,
        boxShadow: T.shadow,
        p: "18px",
        minWidth: 0,
        width: "100%",
      }}
    >
      <Box
        sx={{
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: ".05em",
          textTransform: "uppercase",
          color: T.ink500,
          mb: "10px",
        }}
      >
        Exceptions at lock ({exceptions.length})
      </Box>
      <ExceptionsAtLockList exceptions={exceptions} />
    </Box>
  ) : null;

  // The lock's notes (lock_notes) — always shown, top of the left column, so
  // the desk reads the requester's notes before confirming.
  const notesCard = (
    <Box
      sx={{
        background: "#fff",
        border: `1px solid ${T.line}`,
        borderRadius: T.radius,
        boxShadow: T.shadow,
        p: "14px 18px",
        minWidth: 0,
        width: "100%",
        boxSizing: "border-box",
      }}
    >
      <Box
        sx={{
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: ".05em",
          textTransform: "uppercase",
          color: T.ink500,
          mb: "6px",
        }}
      >
        Lock notes
      </Box>
      <Box
        sx={{
          fontSize: 13,
          lineHeight: 1.5,
          color: lock?.lock_notes ? T.ink900 : T.ink300,
          whiteSpace: "pre-wrap",
          overflowWrap: "anywhere",
        }}
      >
        {lock?.lock_notes || "No lock notes."}
      </Box>
    </Box>
  );

  // The form, in its own card.
  const formCard = (
    <Box
      sx={{
        background: "#fff",
        border: `1px solid ${T.line}`,
        borderRadius: T.radius,
        boxShadow: T.shadow,
        p: "20px 22px",
        minWidth: 0,
        width: "100%",
        maxWidth: 600,
        boxSizing: "border-box",
        // Contain the form so no inner element (e.g. a callout) can spill past
        // the card edge.
        overflow: "hidden",
        justifySelf: "stretch",
      }}
    >
      <LoanLockFormV2
        loanDrilldown={syntheticDrilldown}
        onSuccess={handleDone}
        onCancel={() => onCollapse?.(rowId)}
        hideActionNeeded
        hideLockNotes
      />
    </Box>
  );

  // Inner width cap so the whole expanded block (header + columns) stays centered
  // in the (wide) row rather than hugging the left edge.
  const innerMaxWidth = 1080;
  // const innerMaxWidth = showExceptions ? 1080 : 600;

  return (
    <Box
      role="gridcell"
      className="full-width-panel"
      sx={{
        position: "sticky",
        left: 0,
        width: maxWidth ? `${maxWidth}px` : "100%",
        maxWidth: maxWidth ? `${maxWidth}px` : "100%",
        boxSizing: "border-box",
        overflow: "hidden",

        background: "#fbfcfd",
        borderTop: `1px solid ${T.line}`,
        borderBottom: `4px solid ${T.bg}`,
        // Generous bottom padding so the expanded content breathes.
        p: "18px 22px 56px",
      }}
    >
      <Box sx={{ maxWidth: innerMaxWidth, mx: "auto", width: "100%" }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "12px",
            mb: "18px",
            width: "100%",
          }}
        >
          <Box
            sx={{
              fontSize: 18,
              fontWeight: 700,
              color: T.ink900,
              letterSpacing: "-.01em",
              minWidth: 0,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {lock?.loanName || `#${lock?.loanNumber}`}
          </Box>
          {dealId && (
            <Box sx={{ display: "flex", gap: "8px", flexShrink: 0 }}>
              <OpenLoanNewTabBtn dealId={dealId} />
              <ViewDetailsBtn onClick={() => onViewDetails(dealId)} />
            </Box>
          )}
        </Box>

        {/* Action-needed spans full width, above both the exceptions and form
            cards. */}
        {action && (
          <Box sx={{ mb: "18px" }}>
            <ActionNeededBanner action={action} />
          </Box>
        )}

        {/* Left column: lock notes (always, on top) then exceptions at lock
            when there are any. Right column: the form. */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              lg: "minmax(0, 1fr) minmax(0, 600px)",
            },
            gap: "24px",
            alignItems: "start",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "18px",
              minWidth: 0,
            }}
          >
            {notesCard}
            {exceptionsCard}
          </Box>
          {formCard}
        </Box>
      </Box>
    </Box>
  );
};

// ── full-width queue table (default `/lock-queue`) — AG Grid ──────────────────
// Two-line "Loan" cell, extracted so it can be the agGroupCellRenderer's inner
// renderer (the group renderer supplies the expand/collapse chevron). Shows the
// loan name (primary) with loan # + requester underneath.
const LoanCell = (p) => {
  const reqBy = personName(p.data?.lock_request_user);
  const num = p.data?.loanNumber != null ? `#${p.data.loanNumber}` : null;
  const sub = [num, reqBy ? `Requested by ${reqBy}` : null]
    .filter(Boolean)
    .join(" · ");
  return (
    <Box
      sx={{
        lineHeight: 1.3,
        minWidth: 0,
        width: "100%",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
      }}
    >
      <Box
        sx={{
          fontWeight: 600,
          color: T.ink900,
          fontSize: 13,
          minWidth: 0,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {p.data?.loanName || num || "—"}
      </Box>
      {sub && (
        <Box
          sx={{
            fontSize: 11.5,
            color: T.ink500,
            minWidth: 0,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {sub}
        </Box>
      )}
    </Box>
  );
};

// "Alerts" cell — surfaces action-needed + exceptions-at-lock as compact chips
// (hover for the reason / exception categories).
const AlertsCell = ({ lock }) => {
  const action = actionNeededOf(lock);
  const exceptions = excListOf(lock);
  if (!action && exceptions.length === 0) return <Dash />;
  const anyHard = exceptions.some((e) => e?.isHardException);
  const excTitle = exceptions
    .map((e) => e?.exceptionCategory || "Exception")
    .join(", ");
  return (
    <Box
      sx={{
        display: "flex",
        gap: "6px",
        flexWrap: "nowrap",
        alignItems: "center",
        height: "100%",
        minWidth: 0,
        overflow: "hidden",
      }}
    >
      {action && (
        <Tooltip
          title={
            action.reason
              ? `Returned for action: ${action.reason}`
              : "Returned for action"
          }
          arrow
        >
          <Box component="span" sx={{ flexShrink: 0 }}>
            <Tag bg={ACTION_NEEDED.bg} fg={ACTION_NEEDED.fg}>
              Returned
            </Tag>
          </Box>
        </Tooltip>
      )}
      {exceptions.length > 0 && (
        <Tooltip title={excTitle} arrow>
          <Box component="span" sx={{ flexShrink: 0 }}>
            <Tag
              bg={anyHard ? "#fdecec" : "#fef3e2"}
              fg={anyHard ? T.red : T.amber}
            >
              {exceptions.length}{" "}
              {exceptions.length === 1 ? "exception" : "exceptions"}
            </Tag>
          </Box>
        </Tooltip>
      )}
    </Box>
  );
};

const FullQueueView = ({
  locks,
  filtered,
  stats,
  laneCounts,
  lane,
  setLane,
  search,
  setSearch,
  loading,
  error,
  onRefresh,
  onSelect,
  onReload,
  mode,
  setMode,
  hideInactive,
  setHideInactive,
  hiddenCount,
  loanIndexLoaded,
}) => {
  const gridRef = useRef(null);
  const wrapperRef = useRef(null);
  // Width for the sticky full-width detail panel — tracked off the grid wrapper
  // so the expanded form spans the viewport, not the (wide) scroll area.
  const [wrapperWidth, setWrapperWidth] = useState(null);
  useEffect(() => {
    const el = wrapperRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const obs = new ResizeObserver((entries) => {
      const width = entries[0]?.contentRect?.width;
      if (width) setWrapperWidth(Math.floor(width));
    });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  // Collapse a master row from inside its own detail panel (Cancel / on confirm).
  const handleCollapse = useCallback((rowId) => {
    const node = gridRef.current?.api?.getRowNode(rowId);
    node?.setExpanded(false);
  }, []);

  const detailCellRenderer = useCallback(LockDetailRenderer, []);
  const detailCellRendererParams = useMemo(
    () => ({
      onViewDetails: onSelect,
      onCollapse: handleCollapse,
      onReload,
      maxWidth: wrapperWidth,
    }),
    [onSelect, handleCollapse, onReload, wrapperWidth],
  );

  // AG Grid columns: `valueGetter`/`field` provide the sortable value, function
  // `cellRenderer`s provide the rich chip/two-line display.
  const columnDefs = useMemo(
    () => [
      {
        headerName: "Loan",
        field: "loanNumber",
        flex: 2.4,
        minWidth: 280,
        // agGroupCellRenderer adds the expand/collapse chevron; LoanCell is the
        // two-line inner content. Center the chevron + content vertically.
        cellRenderer: "agGroupCellRenderer",
        cellRendererParams: { innerRenderer: LoanCell },
        cellStyle: { display: "flex", alignItems: "center", height: "100%" },
        filter: "agTextColumnFilter",
        filterValueGetter: (p) =>
          `${p.data?.loanNumber ?? ""} ${p.data?.loanName ?? ""}`,
      },
      ...loanStageColumnDefs({ loaded: loanIndexLoaded }),
      {
        headerName: "Product",
        flex: 0.7,
        minWidth: 110,
        valueGetter: (p) => productOf(p.data),
        filter: "agSetColumnFilter",
        filterParams: {
          valueFormatter: (p) => PRODUCT_META[p.value]?.label || p.value || "—",
        },
        cellRenderer: (p) => <ProductTag product={p.value} />,
      },
      {
        headerName: "Lock type",
        flex: 0.9,
        minWidth: 130,
        valueGetter: (p) => lockChannelOf(p.data),
        filter: "agSetColumnFilter",
        cellRenderer: (p) => <LockChannelTag channel={p.value} />,
      },
      {
        headerName: "Status",
        field: "lock_status",
        flex: 0.9,
        minWidth: 130,
        filter: "agSetColumnFilter",
        cellRenderer: (p) => <StatusChip status={p.value} />,
      },
      {
        headerName: "Lane",
        field: "queueState",
        flex: 1,
        minWidth: 160,
        filter: "agSetColumnFilter",
        filterParams: {
          valueFormatter: (p) => LANE_META[p.value]?.label || p.value || "—",
        },
        cellRenderer: (p) => <LaneChip state={p.value} />,
      },
      {
        headerName: "Alerts",
        flex: 1.4,
        minWidth: 200,
        // Sort by severity: hard exc / action first, then exception count.
        valueGetter: (p) => {
          const exc = excListOf(p.data);
          const hard = exc.some((e) => e?.isHardException) ? 100 : 0;
          return (actionNeededOf(p.data) ? 1000 : 0) + hard + exc.length;
        },
        cellRenderer: (p) => <AlertsCell lock={p.data} />,
      },
      {
        headerName: "Amount",
        flex: 0.9,
        minWidth: 110,
        valueGetter: (p) => loanAmountOf(p.data),
        filter: "agNumberColumnFilter",
        valueFormatter: (p) => (p.value != null ? formatDollar(p.value) : "—"),
      },
      {
        headerName: "Expires",
        flex: 0.9,
        minWidth: 110,
        // sortable numeric: expired first, no-date last.
        valueGetter: (p) => {
          const e = expirationInfo(p.data);
          if (e.state === "expired") return 0;
          if (e.state === "none") return Number.MAX_SAFE_INTEGER;
          return e.date ? e.date.getTime() : Number.MAX_SAFE_INTEGER;
        },
        cellRenderer: (p) => <ExpiresLabel lock={p.data} />,
        // Filter on the expiration DATE (the value above is only a sort key).
        filter: "agDateColumnFilter",
        filterValueGetter: (p) => p.data?.lock_expiration_date || "",
        filterParams: { comparator: dateOnlyComparator },
      },
    ],
    [loanIndexLoaded],
  );

  return (
    <Box
      sx={{
        background: T.bg,
        // Grow to fill the flex-column dashboard main even when content is short
        // (minHeight:100% alone leaves a white gap below short content); still
        // expands past the viewport when the table is tall.
        flex: "1 0 auto",
        minHeight: "100%",
        fontFamily: "inherit",
      }}
    >
      <Container maxWidth="xl" sx={{ pt: "36px", pb: "72px" }}>
        {/* Header */}
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
          <Box>
            <Box
              sx={{
                fontSize: 23,
                fontWeight: 700,
                letterSpacing: "-.01em",
                color: T.ink900,
              }}
            >
              Lock Queue
            </Box>
            <Box sx={{ color: T.ink500, mt: "5px", fontSize: 13.5 }}>
              Review, confirm, and manage every rate lock across correspondents.
            </Box>
          </Box>
          <Box
            sx={{
              ml: "auto",
              display: "flex",
              gap: 1.25,
              alignItems: "center",
            }}
          >
            <RefreshButton onClick={onRefresh} loading={loading} />
          </Box>
        </Box>

        {/* Top — pipeline + include dead / inactive / closed */}
        <QueueScopeRow label="Pipeline">
          <ModeTabs mode={mode} setMode={setMode} />
          <ToolbarSwitch
            checked={!hideInactive}
            onChange={(on) => setHideInactive(!on)}
            label="Include Dead / Inactive / Closed Loans"
            note={
              hideInactive && hiddenCount > 0 ? `(${hiddenCount} Hidden)` : null
            }
            tooltip={INACTIVE_TOOLTIP}
          />
        </QueueScopeRow>

        {/* Middle — widgets */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "repeat(2,1fr)", md: "repeat(4,1fr)" },
            gap: 2,
            m: "18px 0",
          }}
        >
          <StatCard
            n={stats.pending}
            label="Pending confirmation"
            color={T.amber}
          />
          <StatCard n={stats.active} label="Active locks" color={T.green} />
          <StatCard
            n={stats.needsReview}
            label="Needs pricing review"
            color={T.orange}
          />
          <StatCard
            n={stats.expiringSoon}
            label="Expiring ≤ 7 days"
            color={T.red}
          />
        </Box>

        {/* Bottom — lane pills + count + search */}
        <QueueStatusBar
          pills={
            <PillGroup
              options={LANE_PILLS}
              value={lane}
              onChange={setLane}
              counts={laneCounts}
            />
          }
          shown={filtered.length}
          total={laneCounts[ALL] ?? 0}
          search={search}
          onSearch={setSearch}
          placeholder="Search loan #, counterparty, requester…"
        />

        {/* Table (AG Grid) */}
        <Box
          sx={{
            background: T.card,
            border: `1px solid ${T.line}`,
            borderRadius: T.radius,
            boxShadow: T.shadow,
            overflow: "hidden",
          }}
        >
          {error ? (
            <Box sx={{ p: 4, color: T.red, fontSize: 13.5 }}>{error}</Box>
          ) : loading ? (
            <Box sx={{ display: "grid", placeItems: "center", py: 8 }}>
              <CircularProgress size={26} />
            </Box>
          ) : filtered.length === 0 ? (
            <EmptyLocks hasAny={locks.length > 0} />
          ) : (
            <Box
              ref={wrapperRef}
              sx={{
                width: "100%",
                minWidth: 0,
                // Center the group cell (expand chevron + content) vertically and
                // let the inner content shrink so the loan name can ellipsis.
                "& .ag-cell-wrapper": { alignItems: "center", minWidth: 0 },
                "& .ag-cell-wrapper > .ag-group-value": {
                  flex: 1,
                  minWidth: 0,
                  overflow: "hidden",
                },
              }}
            >
              <AgGridTableWrapper height="auto" noBorder noBorderRadius>
                <AgGridReact
                  ref={gridRef}
                  rowData={filtered}
                  columnDefs={columnDefs}
                  defaultColDef={{
                    resizable: true,
                    sortable: true,
                    suppressHeaderMenuButton: true,
                    floatingFilter: true,
                    minWidth: 90,
                    // Vertically center every cell's content (custom two-line
                    // renderers like Loan/Price otherwise top-align).
                    cellStyle: { display: "flex", alignItems: "center" },
                  }}
                  getRowId={(p) => lockKey(p.data)}
                  domLayout="autoHeight"
                  pagination
                  paginationPageSize={25}
                  rowHeight={54}
                  headerHeight={40}
                  floatingFiltersHeight={38}
                  suppressCellFocus
                  // Expandable rows: each row opens an inline lock panel (confirm
                  // form for pending, read-only summary otherwise). Row click
                  // toggles; "View more details" inside navigates to the loan.
                  masterDetail
                  detailCellRenderer={detailCellRenderer}
                  detailCellRendererParams={detailCellRendererParams}
                  detailRowAutoHeight
                  keepDetailRows
                  rowStyle={{ cursor: "pointer" }}
                  onRowClicked={(e) => e.node.setExpanded(!e.node.expanded)}
                />
              </AgGridTableWrapper>
            </Box>
          )}
        </Box>
      </Container>
    </Box>
  );
};

// ── compact list row (column 1 of the master-detail) ─────────────────────────
const CompactLockRow = ({ lock, selected, onClick }) => {
  const product = productOf(lock);
  const exp = expirationInfo(lock);
  const ss = SPLIT_STATUS[lock.lock_status] || { dot: C.text3 };
  const review = lock.queueState === LOCK_QUEUE_STATES.PENDING_CONFIRMATION;
  const urg = splitUrgency(exp);
  const prodLabel =
    PRODUCT_META[product]?.label || (product ? product.toUpperCase() : "—");
  const cp = lock.lock_counterparty;

  // Right-side pill: "Review" for pending, else "Nd left" when expiring soon.
  let pill = null;
  if (review)
    pill = { text: "Review", fg: C.dangerT, bg: C.dangerBg, icon: true };
  else if (exp.state === "expired")
    pill = { text: "Expired", fg: C.dangerT, bg: C.dangerBg };
  else if (urg.cc === "red" || urg.cc === "amber")
    pill = { text: exp.label, fg: urg.fg, bg: urg.bg };

  return (
    <Box
      onClick={onClick}
      sx={{
        cursor: "pointer",
        p: "10px 13px 10px 11px",
        borderBottom: `0.5px solid ${C.border}`,
        borderLeft: `2px solid ${selected ? ss.dot : "transparent"}`,
        background: selected ? C.surface2 : "transparent",
        transition: "background .12s",
        "&:hover": { background: C.surface2 },
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "6px",
          mb: "4px",
        }}
      >
        <Box
          sx={{
            fontSize: "10.5px",
            fontWeight: 600,
            color: C.text,
            minWidth: 0,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {lock.loanNumber != null ? `#${lock.loanNumber}` : "—"}
        </Box>
        {pill && (
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: "3px",
              fontSize: 11,
              fontWeight: 600,
              color: pill.fg,
              background: pill.bg,
              borderRadius: "6px",
              px: "7px",
              py: "1.5px",
              whiteSpace: "nowrap",
              flexShrink: 0,
            }}
          >
            {pill.icon && <WarningAmberRounded sx={{ fontSize: 12 }} />}
            {pill.text}
          </Box>
        )}
      </Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: "7px" }}>
        <Box
          sx={{
            width: 7,
            height: 7,
            borderRadius: "50%",
            background: ss.dot,
            flexShrink: 0,
          }}
        />
        <Box sx={{ fontSize: 10, color: C.text2 }}>
          {lock.lock_status || "—"}
        </Box>
        <Box
          sx={{
            ml: "auto",
            fontSize: 10,
            color: C.text3,
            whiteSpace: "nowrap",
          }}
        >
          {prodLabel}
          {cp ? ` · ${cp}` : ""}
        </Box>
      </Box>
    </Box>
  );
};

// ── lock info pane (column 2 of the master-detail) ───────────────────────────
const InfoLabel = ({ children }) => (
  <Box
    sx={{
      fontSize: "10.5px",
      fontWeight: 700,
      // color: C.text3,
      letterSpacing: ".05em",
      textTransform: "uppercase",
      mt: "26px",
      mb: "4px",
    }}
  >
    {children}
  </Box>
);

const KV = ({ k, v, strong, mono, empty }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: "12px",
      py: "7px",
      borderBottom: `0.5px solid ${C.border}`,
    }}
  >
    <Box sx={{ fontSize: 10.5, color: C.text2, flexShrink: 0 }}>{k}</Box>
    <Box
      sx={{
        fontSize: 10.5,
        // fontSize: 13,
        color: empty ? C.text3 : C.text,
        fontWeight: strong ? 600 : 400,
        fontFamily: mono ? MONO : "inherit",
        textAlign: "right",
        minWidth: 0,
        wordBreak: "break-word",
      }}
    >
      {empty ? "—" : v}
    </Box>
  </Box>
);

// Full lock detail for the selected loan, rendered from the lock doc already in
// the queue payload (no extra fetch). Counterparty is shown here because the
// whole Lock Queue is capital-markets-gated (the queue is the CM workspace).
const LockInfoPane = ({ lock, onEditLock }) => {
  // Prefer the live loan name from the embedded drilldown (state.loanDrilldown,
  // loaded by column 3 for this same loan); fall back to the lock's own name.
  const drilldownName = useSelector((s) => s.loanDrilldown?.loanName);
  const [showJson, setShowJson] = useState(false);
  // Sizer downloads go through the authenticated backend, not the blob URL
  // directly (the storage account isn't public — direct links 403).
  const { getFileById } = useLoanFilesHook();
  if (!lock)
    return (
      <Box sx={{ p: 4, textAlign: "center", color: C.text3, fontSize: 13 }}>
        Select a lock to see its details.
      </Box>
    );

  const dealId = oid(lock.dealId);
  const product = productOf(lock);
  const exp = expirationInfo(lock);
  const ss = SPLIT_STATUS[lock.lock_status] || { fg: C.text2, bg: C.surface2 };
  const review = lock.queueState === LOCK_QUEUE_STATES.PENDING_CONFIRMATION;
  const urg = splitUrgency(exp);
  const { address } = parseLoanName(lock.loanName, lock.loanNumber);
  const subLine = drilldownName || address;
  const amount = loanAmountOf(lock);
  const sizer = lock.loan_sizer;
  const actionReason = lock.actionNeeded?.reason || lock.returnReason;
  const lockedBy = lockedByOf(lock);

  // actionNeeded (returned) wins over the awaiting-confirmation state — red
  // "Returned for action" is the consistent, blocking treatment everywhere.
  const statusPill = lock.actionNeeded
    ? { text: ACTION_NEEDED.title, fg: ACTION_NEEDED.fg, bg: ACTION_NEEDED.bg }
    : review
      ? { text: "Awaiting confirmation", fg: C.warnT, bg: C.warnBg }
      : { text: lock.lock_status || "—", fg: ss.fg, bg: ss.bg };

  const cdNum =
    exp.state === "expired"
      ? "Expired"
      : exp.state === "none"
        ? "Not set"
        : `${exp.days} days left`;
  const cdLbl =
    exp.state === "none"
      ? "Expiration"
      : `Expires ${medDate(lock.lock_expiration_date) || "—"}`;

  return (
    <Box sx={{ p: "16px 18px" }}>
      {/* Header */}
      <Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "10px",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "9px",
              minWidth: 0,
            }}
          >
            <Box
              sx={{
                fontSize: 19,
                fontWeight: 600,
                color: C.text,
                whiteSpace: "nowrap",
              }}
            >
              #{lock.loanNumber}
            </Box>
            <Box
              sx={{
                fontSize: 11,
                fontWeight: 600,
                color: statusPill.fg,
                background: statusPill.bg,
                borderRadius: "7px",
                px: "9px",
                py: "2.5px",
                whiteSpace: "nowrap",
              }}
            >
              {statusPill.text}
            </Box>
          </Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "6px",
              flexShrink: 0,
            }}
          >
            <Tooltip title={showJson ? "Hide raw JSON" : "View raw JSON"} arrow>
              <Box
                component="button"
                type="button"
                onClick={() => setShowJson((v) => !v)}
                aria-label="Toggle raw JSON"
                sx={{
                  width: 26,
                  height: 24,
                  flexShrink: 0,
                  borderRadius: C.radiusSm,
                  border: `0.5px solid ${showJson ? C.navy : C.border2}`,
                  background: showJson ? C.navy : "#fff",
                  color: showJson ? "#fff" : C.text2,
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  "&:hover": {
                    background: showJson ? C.navyHover : C.surface2,
                  },
                }}
              >
                <CodeRounded sx={{ fontSize: 14 }} />
              </Box>
            </Tooltip>
            {dealId && (
              <Tooltip title="Open loan in new tab" arrow>
                <Box
                  component="a"
                  href={`/loan/${dealId}/loan-lock`}
                  target="_blank"
                  rel="noreferrer"
                  aria-label="Open loan in new tab"
                  sx={{
                    width: 26,
                    height: 24,
                    flexShrink: 0,
                    borderRadius: C.radiusSm,
                    border: `0.5px solid ${C.border2}`,
                    background: "#fff",
                    color: C.text2,
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                    textDecoration: "none",
                    "&:hover": { background: C.surface2 },
                  }}
                >
                  <OpenInNewRounded sx={{ fontSize: 14 }} />
                </Box>
              </Tooltip>
            )}
          </Box>
        </Box>
        {subLine && (
          <Box
            sx={{
              fontSize: 12,
              color: C.text2,
              mt: "8px",
              lineHeight: 1.5,
            }}
          >
            {subLine}
          </Box>
        )}
      </Box>

      {showJson ? (
        <Box sx={{ mt: "14px" }}>
          <JsonPreview values={lock} sticky={false} />
        </Box>
      ) : (
        <>
          <Box sx={{ height: "1px", background: C.border, my: "13px" }} />

          {/* actionNeeded (returned) → red "Returned for action"; a pending lock
              with no flag → amber awaiting-confirmation note. */}
          {lock.actionNeeded ? (
            <Box
              sx={{
                background: ACTION_NEEDED.bg,
                border: `0.5px solid ${ACTION_NEEDED.border}`,
                borderRadius: C.radiusSm,
                p: "10px 12px",
                mb: "13px",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  mb: "3px",
                  flexWrap: "wrap",
                }}
              >
                <FlagOutlined sx={{ fontSize: 16, color: ACTION_NEEDED.fg }} />
                <Box sx={{ fontSize: 13, fontWeight: 700, color: ACTION_NEEDED.fg }}>
                  {ACTION_NEEDED.title}
                </Box>
                {lock.actionNeeded?.requiresException && (
                  <Box
                    component="span"
                    sx={{
                      fontSize: 10.5,
                      fontWeight: 700,
                      color: ACTION_NEEDED.fg,
                      background: "#fff",
                      border: `1px solid ${ACTION_NEEDED.border}`,
                      borderRadius: "999px",
                      px: "7px",
                      py: "1px",
                    }}
                  >
                    Exception required
                  </Box>
                )}
              </Box>
              <Box sx={{ fontSize: 12, lineHeight: 1.5, color: ACTION_NEEDED.ink }}>
                {actionReason}
              </Box>
            </Box>
          ) : review ? (
            <Box
              sx={{
                background: C.warnBg,
                border: `0.5px solid ${C.warnT}33`,
                borderRadius: C.radiusSm,
                p: "10px 12px",
                mb: "13px",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  mb: "3px",
                }}
              >
                <WarningAmberRounded sx={{ fontSize: 16, color: C.warnT }} />
                <Box sx={{ fontSize: 13, fontWeight: 600, color: C.warnT }}>
                  Awaiting confirmation
                </Box>
              </Box>
              <Box sx={{ fontSize: 12, lineHeight: 1.5, color: C.warnT }}>
                Review the requested terms, then confirm or return this lock.
              </Box>
            </Box>
          ) : null}

          {/* Countdown */}
          <Box
            sx={{
              background: urg.bg,
              borderRadius: C.radiusSm,
              p: "11px 13px",
              display: "flex",
              alignItems: "center",
            }}
          >
            <Box>
              <Box sx={{ fontSize: 11, mb: "4px", color: urg.fg }}>{cdLbl}</Box>
              <Box
                sx={{
                  fontSize: 16,
                  fontWeight: 600,
                  lineHeight: 1.5,
                  color: urg.fg,
                }}
              >
                {cdNum}
              </Box>
            </Box>
            <Box sx={{ ml: "auto", color: urg.fg, display: "flex" }}>
              <ScheduleRounded sx={{ fontSize: 22 }} />
            </Box>
          </Box>

          {/* Price terms */}
          <InfoLabel>Price terms</InfoLabel>
          {product === "rtl" ? (
            <>
              <KV
                k="Coupon"
                v={
                  lock.coupon_rate != null
                    ? formatPercent(lock.coupon_rate)
                    : null
                }
                strong
                mono
                empty={lock.coupon_rate == null}
              />
              <KV
                k="Loan amount"
                v={amount != null ? formatDollar(amount) : null}
                strong
                empty={amount == null}
              />
              <KV
                k="Buy rate"
                v={
                  lock.override_buy_rate != null
                    ? formatPercent(lock.override_buy_rate)
                    : null
                }
                mono
                empty={lock.override_buy_rate == null}
              />
              <KV
                k="Counterparty"
                v={lock.lock_counterparty}
                empty={!lock.lock_counterparty}
              />
            </>
          ) : (
            <>
              <KV
                k="Rate at lock"
                v={
                  lock.rate_at_lock != null
                    ? formatPercent(lock.rate_at_lock)
                    : null
                }
                strong
                mono
                empty={lock.rate_at_lock == null}
              />
              <KV
                k="Loan amount"
                v={amount != null ? formatDollar(amount) : null}
                strong
                empty={amount == null}
              />
              <KV
                k="Calculated DSCR"
                v={
                  lock.calculated_dscr_at_lock != null
                    ? `${lock.calculated_dscr_at_lock}x`
                    : null
                }
                mono
                empty={lock.calculated_dscr_at_lock == null}
              />
              <KV
                k="Counterparty"
                v={lock.lock_counterparty}
                empty={!lock.lock_counterparty}
              />
            </>
          )}

          {/* Lock timeline */}
          <InfoLabel>Lock timeline</InfoLabel>
          <KV
            k="Requested"
            v={shortDate(lock.lock_requested_date)}
            empty={!shortDate(lock.lock_requested_date)}
          />
          <KV
            k="Confirmed"
            v={shortDate(lock.lock_confirm_date)}
            empty={!shortDate(lock.lock_confirm_date)}
          />
          <KV
            k="Lock type"
            v={lockChannelOf(lock)}
            empty={!lockChannelOf(lock)}
          />
          <KV
            k="Lock period"
            v={
              lock.lock_period_days != null
                ? `${lock.lock_period_days} days`
                : null
            }
            empty={lock.lock_period_days == null}
          />
          <KV k="Originator" v={lock.originator} empty={!lock.originator} />
          <KV k="Locked by" v={lockedBy} empty={!lockedBy} />

          {/* Every free-text field on the lock (lock notes always; reasons when set) */}
          <>
            <InfoLabel>Notes</InfoLabel>
            {[
              ["Lock notes", lock.lock_notes],
              ["Extension reason", lock.pendingExtension?.reason],
              ["Cancellation reason", lock.cancellation?.reason],
            ]
              .filter(([, v], i) => i === 0 || v)
              .map(([k, v]) => (
                <Box
                  key={k}
                  sx={{ py: "7px", borderBottom: `0.5px solid ${C.border}` }}
                >
                  <Box sx={{ fontSize: 10.5, color: C.text2 }}>{k}</Box>
                  <Box
                    sx={{
                      fontSize: 10.5,
                      color: v ? C.text : C.text3,
                      mt: "3px",
                      whiteSpace: "pre-wrap",
                      overflowWrap: "anywhere",
                      // Filled notes get the gray box so they stand
                      // out from the key/value rows around them.
                      ...(v && {
                        fontSize: 12.5,
                        fontWeight: 500,
                        lineHeight: 1.5,
                        mt: "6px",
                        background: "#F8FAFC",
                        border: "1px solid #E2E8F0",
                        borderRadius: "8px",
                        p: "8px 10px",
                      }),
                    }}
                  >
                    {v || "—"}
                  </Box>
                </Box>
              ))}
          </>

          {/* Loan sizer */}
          <InfoLabel>Loan sizer</InfoLabel>
          {sizer ? (
            <Box
              component="button"
              type="button"
              onClick={() =>
                getFileById({
                  loanId: oid(sizer.dealId) || dealId,
                  file_storage_name: sizer.file_storage_name,
                  fileDownloadName:
                    sizer.file_display_name || sizer.file_name_as_loaded,
                  fileDownload: true,
                  isFile: true,
                })
              }
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "10px",
                width: "100%",
                p: "10px 11px",
                background: C.surface2,
                border: "none",
                borderRadius: C.radiusSm,
                mt: "8px",
                font: "inherit",
                textAlign: "left",
                cursor: "pointer",
                "&:hover": { background: "#eef1f4" },
              }}
            >
              <InsertDriveFileOutlined
                sx={{ fontSize: 16, color: C.successT, flexShrink: 0 }}
              />
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Box
                  sx={{
                    fontSize: "10.5px",
                    fontWeight: 600,
                    color: C.text,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {sizer.file_display_name ||
                    sizer.file_name_as_loaded ||
                    "Sizer"}
                </Box>
                {/* <Box sx={{ fontSize: 11, color: C.text3 }}>
                  Loan sizer
                  {sizer.size != null ? ` · ${fmtMB(sizer.size)}` : ""}
                </Box> */}
              </Box>
              <FileDownloadOutlined
                sx={{ fontSize: 17, color: C.text2, flexShrink: 0 }}
              />
            </Box>
          ) : (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                p: "10px 11px",
                background: C.surface2,
                borderRadius: C.radiusSm,
                mt: "8px",
                color: C.text3,
                fontSize: 12,
              }}
            >
              <InsertDriveFileOutlined sx={{ fontSize: 19 }} />
              No sizer uploaded
            </Box>
          )}

          {/* Exceptions at lock */}
          {excListOf(lock).length > 0 && (
            <>
              <InfoLabel>
                Exceptions at lock ({excListOf(lock).length})
              </InfoLabel>
              <Box sx={{ mt: "8px" }}>
                <ExceptionsAtLockList exceptions={excListOf(lock)} />
              </Box>
            </>
          )}

          {/* Additional info */}
          <InfoLabel>Additional info</InfoLabel>
          <KV
            k="Queue state"
            v={prettyEnum(lock.queueState)}
            empty={!lock.queueState}
          />
          <KV
            k="Pricing review"
            v={lock.requiresPricingReview ? "Required" : "Not required"}
          />
          <KV
            k="Locked by (SF)"
            v={lock.salesforce_locked_by}
            empty={!lock.salesforce_locked_by}
          />
          <KV
            k="SF lock period"
            v={
              lock.salesforce_lock_period_days != null
                ? `${lock.salesforce_lock_period_days} days`
                : null
            }
            empty={lock.salesforce_lock_period_days == null}
          />
          <KV k="Source" v={prettyEnum(lock.source)} empty={!lock.source} />
          <KV
            k="SF rate-lock ID"
            v={lock.salesforce_rate_lock_id}
            mono
            empty={!lock.salesforce_rate_lock_id}
          />
          <KV
            k="Sizer parse pending"
            v={yesNo(lock.sizerParsePending)}
            empty={lock.sizerParsePending == null}
          />
          <KV
            k="Lock expired"
            v={yesNo(lock.lock_expired)}
            empty={lock.lock_expired == null}
          />
          <KV
            k="Created"
            v={medDateTime(lock.createDateTime)}
            empty={!medDateTime(lock.createDateTime)}
          />
          <KV
            k="Updated"
            v={medDateTime(lock.updateDateTime)}
            empty={!medDateTime(lock.updateDateTime)}
          />
        </>
      )}
    </Box>
  );
};

// ── master-detail (`/lock-queue/:id/*`) ──────────────────────────────────────
// A full-width crumb bar above three columns: (1) the filtered lock list (a
// single-column nav rail / master, not a data table — the full data grid is the
// `/lock-queue` view above), (2) full lock info for the selection, (3) the loan
// drilldown, embedded (header overview + title actions hidden). Columns 1 + 2
// follow the approved mockup palette (C); column 3 is left as-is.
const SplitQueueView = ({
  filtered,
  lane,
  setLane,
  laneCounts,
  search,
  setSearch,
  loading,
  locks,
  onSelect,
  onClose,
}) => {
  const { id: selectedId } = useParams();
  const navigate = useNavigate();

  const selectedLock = useMemo(
    () => locks.find((l) => oid(l.dealId) === selectedId) || null,
    [locks, selectedId],
  );

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        flex: "1 1 0",
        minHeight: 0,
        height: "100%",
        width: "100%",
        background: C.bg,
      }}
    >
      {/* 3-column layout (the crumb lives at the top of column 1) */}
      <Box sx={{ display: "flex", flex: "1 1 0", minHeight: 0 }}>
        {/* Column 1 — crumb + filtered lock list */}
        <Box
          sx={{
            width: 220,
            flexShrink: 0,
            display: "flex",
            flexDirection: "column",
            minHeight: 0,
            background: C.surface,
            borderRight: `0.5px solid ${C.border}`,
          }}
        >
          {/* Crumb — top of the list column */}
          <Box
            sx={{
              flexShrink: 0,
              height: 44,
              borderBottom: `0.5px solid ${C.border}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: "6px",
              px: 1,
              fontSize: 12.5,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.6 }}>
              <Tooltip title="Back to Lock Queue" arrow>
                <Box
                  component="button"
                  type="button"
                  onClick={onClose}
                  aria-label="Back to Lock Queue"
                  sx={{
                    font: "inherit",
                    color: C.tealD,
                    background: "transparent",
                    border: "none",
                    p: 0,
                    cursor: "pointer",
                    display: "inline-flex",
                    alignItems: "center",
                    flexShrink: 0,
                    "&:hover": { opacity: 0.75 },
                  }}
                >
                  <ArrowBackRounded sx={{ fontSize: 16 }} />
                </Box>
              </Tooltip>
              {/* Lane filter (was the "Lock Queue" label), in the crumb green. */}
              <Select
                value={lane}
                onChange={(e) => setLane(e.target.value)}
                variant="standard"
                disableUnderline
                renderValue={(v) =>
                  v === ALL
                    ? "All locks"
                    : LANES.find((l) => l.key === v)?.label || "Locks"
                }
                sx={{
                  fontSize: 12.5,
                  fontWeight: 600,
                  color: C.tealD,
                  "& .MuiSelect-select": {
                    p: 0,
                    pr: "18px !important",
                    minHeight: "unset",
                    color: C.tealD,
                  },
                  "& .MuiSelect-icon": { color: C.tealD, right: -2 },
                  "&:before, &:after": { display: "none" },
                }}
              >
                {LANES.map(({ key, label }) => (
                  <MenuItem key={key} value={key} sx={{ fontSize: 13 }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        width: "100%",
                        gap: 1,
                      }}
                    >
                      <span>{key === ALL ? "All locks" : label}</span>
                      <Box
                        component="span"
                        sx={{
                          ml: "auto",
                          fontSize: 11,
                          color: C.text2,
                          background: C.surface2,
                          borderRadius: "6px",
                          px: "8px",
                          py: "2px",
                        }}
                      >
                        {laneCounts[key] ?? 0}
                      </Box>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </Box>
            <Box component="span" sx={{ color: C.text2 }}>
              {filtered.length} locks
            </Box>
          </Box>
          {/* list head — search only (lane filter moved to the crumb) */}
          <Box
            sx={{
              p: "12px 14px 10px",
              borderBottom: `0.5px solid ${C.border}`,
              flexShrink: 0,
            }}
          >
            <SearchInput value={search} onChange={(e) => setSearch(e)} />
          </Box>

          {/* list */}
          <Box sx={{ flex: "1 1 0", minHeight: 0, overflowY: "auto" }}>
            {loading ? (
              <Box sx={{ display: "grid", placeItems: "center", py: 6 }}>
                <CircularProgress size={22} />
              </Box>
            ) : filtered.length === 0 ? (
              <EmptyLocks hasAny={locks.length > 0} />
            ) : (
              filtered.map((l) => {
                const dealId = oid(l.dealId);
                return (
                  <CompactLockRow
                    key={lockKey(l)}
                    lock={l}
                    selected={dealId === selectedId}
                    onClick={() => dealId && onSelect(dealId)}
                  />
                );
              })
            )}
          </Box>
        </Box>

        {/* Column 2 — lock info for the selection */}
        <Box
          sx={{
            width: 360,
            flexShrink: 0,
            minHeight: 0,
            overflowY: "auto",
            background: "#ffffff",
            // background: "#fbfcfd",
            borderRight: `0.5px solid ${C.border}`,
          }}
        >
          <LockInfoPane
            lock={selectedLock}
            onEditLock={() =>
              selectedId && navigate(`/lock-queue/${selectedId}/loan-lock`)
            }
          />
        </Box>

        {/* Column 3 — embedded loan drilldown (left as-is) */}
        <Box
          sx={{
            position: "relative",
            flex: "1 1 0",
            minWidth: 0,
            height: "100%",
            overflow: "hidden",
            background: "#f1f5f9",
          }}
        >
          <LoanDrilldownScreen embedded />
        </Box>
      </Box>
    </Box>
  );
};

// ── access gate ──────────────────────────────────────────────────────────────
const NoAccessGate = () => (
  <Box
    sx={{
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      gap: 1.25,
      minHeight: "60vh",
      textAlign: "center",
      color: T.ink500,
      p: 4,
    }}
  >
    <LockOutlined sx={{ fontSize: 36, color: T.ink300 }} />
    <Box sx={{ fontSize: 16, fontWeight: 700, color: T.ink900 }}>
      Capital markets only
    </Box>
    <Box sx={{ fontSize: 13.5, maxWidth: 440 }}>
      The lock queue — where counterparties are entered and locks are confirmed
      — is restricted to the capital-markets team.
    </Box>
  </Box>
);

const LockQueueScreen = () => {
  useDocumentTitle("Lock Queue");
  // Resolves lock-request user emails to names via the shared `users` slice —
  // pull it lazily (cache-gated) instead of relying on a global eager fetch.
  useEnsureUsers();
  const navigate = useNavigate();
  const user = useSelector((state) => state.user);
  const users = useSelector((state) => state.users);
  const myEmail = (user?.emailAddress || user?.email || "").toLowerCase();
  const { isCapitalMarkets, isCloser, isSuperAdmin } = usePermissionsHook();
  // Visible to the capital-markets group + closers, plus super admins.
  const canAccess = isCapitalMarkets || isCloser || isSuperAdmin;

  const [locks, setLocks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [lane, setLane] = useState(ALL);
  const [search, setSearch] = useState("");
  // Pre-close (LOS `/pricing/all_locks`) vs post-close (Salesforce-direct,
  // search-driven). The post-close lane has its own view + data source.
  const [mode, setMode] = useState("preclose");
  const [hideInactive, setHideInactive] = useState(() =>
    readHideInactive(HIDE_INACTIVE_KEY),
  );
  // dealId → loan stage / closing date, joined from both pipeline lists. null
  // until loaded (or if both pipeline fetches fail) — nothing is hidden then.
  const [loanIndex, setLoanIndex] = useState(null);

  useEffect(() => {
    writeHideInactive(HIDE_INACTIVE_KEY, hideInactive);
  }, [hideInactive]);

  // Locks + both pipelines load together so the stage filter applies before
  // the first paint (no rows flashing in and then disappearing).
  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    const lockRows = losLocksApi
      .getAllLocks()
      .then((rows) => setLocks(Array.isArray(rows) ? rows : []))
      .catch((err) =>
        setError(
          err?.response?.data?.detail ||
            err?.message ||
            "Failed to load locks.",
        ),
      );
    const pipelines = fetchLoanIndex().then(setLoanIndex);
    Promise.all([lockRows, pipelines]).finally(() => setLoading(false));
  }, []);

  // Every lock stamped with its loan's stage + closing date.
  const rows = useMemo(
    () => locks.map((l) => withLoanStage(l, loanIndex)),
    [locks, loanIndex],
  );
  // The working set — widgets, lane counts and the table all read from it,
  // so locks on hidden loans drop out of the numbers too.
  const activeLocks = useMemo(
    () => (hideInactive ? rows.filter((l) => !l.loanInactive) : rows),
    [rows, hideInactive],
  );
  const hiddenCount = rows.length - activeLocks.length;

  useEffect(() => {
    if (canAccess) load();
  }, [canAccess, load]);

  const stats = useMemo(() => {
    const byLane = (s) => activeLocks.filter((l) => l.queueState === s).length;
    const expiringSoon = activeLocks.filter(
      (l) =>
        l.queueState === LOCK_QUEUE_STATES.ACTIVE &&
        expirationInfo(l).state === "soon",
    ).length;
    return {
      pending: byLane(LOCK_QUEUE_STATES.PENDING_CONFIRMATION),
      active: byLane(LOCK_QUEUE_STATES.ACTIVE),
      needsReview: byLane(LOCK_QUEUE_STATES.NEEDS_PRICING_REVIEW),
      expiringSoon,
    };
  }, [activeLocks]);

  const laneCounts = useMemo(() => {
    const counts = { [ALL]: activeLocks.length };
    LANES.forEach(({ key }) => {
      if (key === ALL) return;
      counts[key] = activeLocks.filter((l) => l.queueState === key).length;
    });
    return counts;
  }, [activeLocks]);

  // Default lane (once, after the first load): Pending if any await confirmation,
  // else Needs review if any, else All. A ref so manual lane changes and later
  // refreshes don't get overridden.
  const didInitLane = useRef(false);
  useEffect(() => {
    if (didInitLane.current || loading || locks.length === 0) return;
    didInitLane.current = true;
    if (laneCounts[LOCK_QUEUE_STATES.PENDING_CONFIRMATION] > 0)
      setLane(LOCK_QUEUE_STATES.PENDING_CONFIRMATION);
    else if (laneCounts[LOCK_QUEUE_STATES.NEEDS_PRICING_REVIEW] > 0)
      setLane(LOCK_QUEUE_STATES.NEEDS_PRICING_REVIEW);
    else setLane(ALL);
  }, [loading, locks, laneCounts]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return activeLocks.filter(
      (l) => (lane === ALL || l.queueState === lane) && matchesSearch(l, q),
    );
  }, [activeLocks, lane, search]);

  // Select a loan → open the master-detail on the Loan Lock tab.
  const handleSelect = useCallback(
    (dealId) => navigate(`/lock-queue/${dealId}/loan-lock`),
    [navigate],
  );

  if (!canAccess) return <NoAccessGate />;

  const shared = {
    locks: rows,
    filtered,
    stats,
    laneCounts,
    lane,
    setLane,
    search,
    setSearch,
    loading,
    error,
    users,
    myEmail,
    mode,
    setMode,
    hideInactive,
    setHideInactive,
    hiddenCount,
    loanIndexLoaded: Boolean(loanIndex),
  };

  return (
    <Routes>
      <Route
        index
        element={
          mode === "postclose" ? (
            <PostCloseQueueView mode={mode} setMode={setMode} />
          ) : (
            <FullQueueView
              {...shared}
              onRefresh={load}
              onReload={load}
              onSelect={handleSelect}
            />
          )
        }
      />
      <Route
        path=":id/*"
        element={
          <SplitQueueView
            {...shared}
            onSelect={handleSelect}
            onClose={() => navigate("/lock-queue")}
          />
        }
      />
    </Routes>
  );
};

export default LockQueueScreen;
